import { createApi } from '@reduxjs/toolkit/query/react';
import { baseApiQuery } from '../basequery';

export interface GetEmployeePhotoArgs {
  oid: number | string;
}

export interface COIEmployee {
  id: string;
  displayName: string;
  mail: string;
}

export interface GetEmployeeByOidResponse {
  data: {
    employee: COIEmployee;
  };
}

export interface GetCOIEmployeesResponse {
  data: {
    employees: COIEmployee[];
  };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read photo blob'));
    reader.readAsDataURL(blob);
  });
}

export const employeeAPI = createApi({
  reducerPath: 'employeeAPI',
  baseQuery: baseApiQuery,
  tagTypes: ['Employees', 'EmployeePhoto'],
  endpoints: (builder) => ({
    getCOIemployees: builder.query<GetCOIEmployeesResponse, string | undefined>({
      query: (searchTerm) => ({
        url: '/employees',
        method: 'GET',
        params: { q: searchTerm },
      }),
      providesTags: (_result, _error, searchTerm) => {
        return [{ type: 'Employees', id: searchTerm }];
      },
    }),
    getEmployeeByOid: builder.query<GetEmployeeByOidResponse, string>({
      query: (oid) => ({
        url: `/employees/${oid}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, oid) => [{ type: 'Employees', id: oid }],
    }),
    /** Returns a base64 data URL for the employee's profile photo, or null when none exists (404). */
    getEmployeePhoto: builder.query<string | null, GetEmployeePhotoArgs>({
      queryFn: async ({ oid }, api, extraOptions) => {
        const result = await baseApiQuery(
          {
            url: `/employees/${oid}/photo`,
            method: 'GET',
            responseHandler: async (response) => response.blob(),
          },
          api,
          extraOptions
        );

        if (result.error) {
          const status = (result.error as { status?: number | string }).status;
          if (status === 404) {
            // no photo
            return { data: null };
          }
          return { error: result.error };
        }

        try {
          const photoDataUrl = await blobToDataUrl(result.data as Blob);
          return { data: photoDataUrl };
        } catch (err) {
          return {
            error:
              err instanceof Error
                ? {
                    status: 'CUSTOM_ERROR' as const,
                    error: err.message,
                    data: err.message,
                  }
                : {
                    status: 'CUSTOM_ERROR' as const,
                    error: 'Failed to convert photo blob to data URL',
                    data: 'Failed to convert photo blob to data URL',
                  },
          };
        }
      },
      providesTags: (_result, _error, { oid }) => [{ type: 'EmployeePhoto', id: oid }],
    }),
  }),
});

export const {
  useGetCOIemployeesQuery,
  useLazyGetCOIemployeesQuery,
  useGetEmployeeByOidQuery,
  useLazyGetEmployeeByOidQuery,
  useGetEmployeePhotoQuery,
  useLazyGetEmployeePhotoQuery,
} = employeeAPI;
